import { Megaphone } from "lucide-react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { AnnouncementFormData } from "../hooks/use-announcement-text";

interface AnnouncementCardProps {
  form: UseFormReturn<AnnouncementFormData>;
  loading: boolean;
  saving: boolean;
}

export function AnnouncementCard({ form, loading, saving }: AnnouncementCardProps) {
  const { t } = useTranslation("settings");

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <Megaphone className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <CardTitle className="text-lg font-medium">
            {t("storefront.announcement.title")}
          </CardTitle>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 pt-0">
        <p className="text-sm text-muted-foreground">{t("storefront.announcement.description")}</p>

        <div className="space-y-2">
          <Label htmlFor="announcement-text">{t("storefront.announcement.label")}</Label>
          {/* Controller, not `form.register`: React Compiler memoizes `register`, so it
              never re-runs after `form.reset` drops the field and the input stops tracking. */}
          <Controller
            control={form.control}
            name="announcementText"
            render={({ field }) => (
              <Textarea
                id="announcement-text"
                placeholder={t("storefront.announcement.placeholder")}
                rows={3}
                {...field}
                disabled={loading || saving}
              />
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}
